import { state } from '../lib/state.js';
import { saveLineCalculatorSettings } from '../services/repository.js';
import { startCountryBrowserlessJob } from '../services/browserless-country-dispatch.js';
import { els } from '../app/dom.js';
import { requireAuthenticatedSession } from '../app/auth.js';
import { setStatus, localDate, normalizeClock, sectionDistanceKm, formatDuration } from '../lib/app-helpers.js';
import { prepareSectionMetrics } from './common.js';

let pendingEmytoSection = null;
let emytoUi = null;

function ensureEmytoDialog(){
  if(emytoUi)return emytoUi;
  const style=document.createElement('style');
  style.id='d24-emyto-dialog-style';
  style.textContent=`
    #d24EmytoDialog{position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(15,23,42,.58)}
    #d24EmytoDialog.hidden{display:none}
    #d24EmytoDialog .d24-sk-dialog{position:relative;width:min(620px,100%);max-height:90vh;overflow:auto;padding:24px;background:#fff;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.3);color:#172033}
    #d24EmytoDialog h2{margin:0;color:#17663a;font-size:24px}
    #d24EmytoDialog .d24-sk-route{margin:7px 0 18px;padding:10px 12px;background:#f3f8f5;border-radius:9px;color:#405047}
    #d24EmytoDialog .d24-sk-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}
    #d24EmytoDialog label{display:flex;flex-direction:column;gap:6px;font-weight:700;color:#344054}
    #d24EmytoDialog input,#d24EmytoDialog select{min-height:42px;padding:9px 10px;border:1px solid #cbd5e1;border-radius:8px;background:#fff;font:inherit;color:#172033}
    #d24EmytoDialog .d24-sk-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:20px}
    #d24EmytoDialog button{border:0;border-radius:8px;padding:10px 16px;font-weight:700;cursor:pointer}
    #d24EmytoDialog .d24-sk-cancel{background:#e8edf3;color:#26364a}
    #d24EmytoDialog .d24-sk-submit{background:#17663a;color:#fff}
    #d24EmytoDialog .d24-sk-close{position:absolute;right:14px;top:12px;background:transparent;color:#667085;font-size:24px;padding:4px 8px}
    @media(max-width:620px){#d24EmytoDialog .d24-sk-grid{grid-template-columns:1fr}}
  `;
  document.head.appendChild(style);

  const dialog=document.createElement('div');
  dialog.id='d24EmytoDialog';
  dialog.className='hidden';
  dialog.setAttribute('aria-hidden','true');
  dialog.innerHTML=`
    <section class="d24-sk-dialog" role="dialog" aria-modal="true" aria-labelledby="d24EmytoTitle">
      <button type="button" class="d24-sk-close" aria-label="Zamknij">×</button>
      <h2 id="d24EmytoTitle">eMyto Słowacja</h2>
      <div class="d24-sk-route" id="d24EmytoRoute"></div>
      <form id="d24EmytoForm">
        <div class="d24-sk-grid">
          <label>Data obliczenia<input id="d24EmytoDate" type="date" required readonly></label>
          <label>Godzina obliczenia<input id="d24EmytoTime" type="time" step="1" required readonly></label>
          <label>Klasa pojazdu<select id="d24EmytoVehicle" required disabled>
            <option value="112">Autobus 12 t lub więcej</option>
            <option value="135">Autobus 3,5–12 t</option>
          </select></label>
          <label>Norma EURO<select id="d24EmytoEmission" required disabled>
            <option value="0">EURO 0</option><option value="1">EURO I</option><option value="2">EURO II</option>
            <option value="3">EURO III</option><option value="4">EURO IV</option><option value="5">EURO V</option>
            <option value="6">EURO VI</option><option value="7">EEV</option><option value="8">ZEV</option>
          </select></label>
          <label>Klasa CO₂<select id="d24EmytoCo2" required disabled><option value="1">CO₂ 1</option></select></label>
        </div>
        <div class="d24-sk-actions">
          <button type="button" class="d24-sk-cancel">Anuluj</button>
          <button type="submit" class="d24-sk-submit">Uruchom eMyto</button>
        </div>
      </form>
    </section>`;
  document.body.appendChild(dialog);

  emytoUi={
    dialog,
    route:dialog.querySelector('#d24EmytoRoute'),
    form:dialog.querySelector('#d24EmytoForm'),
    date:dialog.querySelector('#d24EmytoDate'),
    time:dialog.querySelector('#d24EmytoTime'),
    vehicle:dialog.querySelector('#d24EmytoVehicle'),
    emission:dialog.querySelector('#d24EmytoEmission'),
    co2:dialog.querySelector('#d24EmytoCo2'),
  };
  dialog.querySelector('.d24-sk-close').addEventListener('click',closeEmytoDialog);
  dialog.querySelector('.d24-sk-cancel').addEventListener('click',closeEmytoDialog);
  dialog.addEventListener('click',event=>{if(event.target===dialog)closeEmytoDialog();});
  emytoUi.form.addEventListener('submit',submitEmytoDialog);
  return emytoUi;
}

export function openEmytoDialog(section){
  pendingEmytoSection=section;
  const ui=ensureEmytoDialog();
  const now=new Date();
  // eMyto SK zawsze liczymy na dzień i godzinę uruchomienia raportu.
  // Parametry produkcyjne są stałe: autobus 12 t+, EURO VI, CO₂ 1.
  ui.date.value=localDate(now);
  ui.time.value=now.toTimeString().slice(0,8);
  ui.vehicle.value='112';
  ui.emission.value='6';
  ui.co2.value='1';
  ui.route.textContent=`${section.countryName}: ${section.points.map(point=>point.name).join(' → ')}`;
  ui.dialog.classList.remove('hidden');
  ui.dialog.setAttribute('aria-hidden','false');
  document.body.style.overflow='hidden';
  setTimeout(()=>ui.form.querySelector('.d24-sk-submit')?.focus(),0);
}

export function closeEmytoDialog(){
  pendingEmytoSection=null;
  if(!emytoUi)return;
  emytoUi.dialog.classList.add('hidden');
  emytoUi.dialog.setAttribute('aria-hidden','true');
  document.body.style.overflow='';
}

export async function submitEmytoDialog(event){
  event.preventDefault();
  if(!pendingEmytoSection)return;
  const ui=ensureEmytoDialog();
  if(!ui.form.reportValidity())return;
  const section=pendingEmytoSection;
  const now=new Date();
  const settings={
    system:'emyto-sk',
    date:localDate(now),
    time:normalizeClock(now.toTimeString().slice(0,8)),
    vehicleCategory:'112',
    emissionClass:'6',
    co2Class:'1',
  };
  try{
    await saveLineCalculatorSettings(
      state.organizationId,
      state.currentRoute.lineNumber,
      'emyto-sk',
      { ...settings }
    );
  }catch(error){
    console.error('[D24 eMyto SK] Nie udało się zapisać parametrów linii:',error);
    return setStatus(els.extensionStatus,`Nie udało się zapisać parametrów eMyto SK dla linii: ${error.message}`,'error');
  }
  closeEmytoDialog();
  await runEmytoSk(section,settings);
}

export async function runEmytoSk(section,settings){
  if(!await requireAuthenticatedSession('uruchomić kalkulator eMyto Słowacja'))return;
  const preparedSection=await prepareSectionMetrics(section,'słowackiego');
  if(!preparedSection)return;
  if((preparedSection.points||[]).length>6){
    const message=`eMyto Słowacja: obecna automatyzacja obsługuje maksymalnie 6 punktów łącznie. Odcinek ma ${preparedSection.points.length}.`;
    window.alert(message);
    return setStatus(els.extensionStatus,message,'warning');
  }

  const distanceKm=sectionDistanceKm(preparedSection);
  const durationText=Number.isFinite(Number(preparedSection.durationSeconds))?formatDuration(Number(preparedSection.durationSeconds)):'';
  const jobId=crypto.randomUUID();
  const payload={
    jobId,
    calculatorKey:'emyto-sk',
    routeId:state.currentRoute.id,
    routeVersionId:state.currentRoute.versionId,
    routeTitle:state.currentRoute.title,
    sourceFile:state.currentRoute.sourceFileName,
    countryCode:'SK',
    countryName:'Słowacja',
    sectionId:preparedSection.id,
    sectionOrder:preparedSection.order,
    distanceKm,
    durationText,
    points:preparedSection.points.map(p=>({
      name:p.name,
      address:p.address||'',
      countryCode:p.countryCode||'',
      countryName:p.countryName||'',
      country:p.countryName||'',
      type:p.isBorder?'Granica':'Przystanek',
      pointType:p.isBorder?'border':'stop',
      lat:p.lat,
      lon:p.lon,
      isBorder:Boolean(p.isBorder),
      borderName:p.borderName||'',
      requestedBorderPair:p.requestedBorderPair||'',
      resolvedDisplayName:p.resolvedDisplayName||'',
      query:`${p.lat}, ${p.lon}`,
    })),
    settings:{
      ...settings,
      system:'emyto-sk',
      vehicleCategory:'112',
      emissionClass:'6',
      co2Class:'1',
    },
  };

  setStatus(els.extensionStatus,`Uruchamiam eMyto Słowacja w Browserless (${preparedSection.points.length} punktów)…`,'working');
  try{await startCountryBrowserlessJob(payload);}catch(error){console.error('[D24 BROWSERLESS eMyto SK]',error);}
}



